<template>
  <PmNotificationContainerPure
    notification-type="resourceRequestFeedback"
    :date="date"
    :is-unread="isUnread"
    :title="`Neue Nachricht von ${props.name}`"
    class="PmNotificationResourceRequestMessagePure"
  >
    <template #content>
      <PmNotePure
        :note="message"
        :compact="false"
        :subject="null"
        name="Nachricht"
      />
    </template>

    <template #meta>
      <PmMetaListItemPure label="Projekt">
        {{ projectNumber }} {{ projectName }}

        <template v-if="jobs">
          <template v-if="isSingleJob">
            für einen Job am {{ timeframe }}</template
          >

          <template v-else>
            für {{ jobs.length }} Jobs zwischen {{ timeframe }}
          </template>
        </template>
      </PmMetaListItemPure>
    </template>

    <template #actions>
      <PmButtonListPure>
        <PmButtonPure label="Details" @click="emit('openDetails')" />

        <PmButtonPure
          icon="jump"
          :title="jumpButtonTitle"
          :disabled="isJumpDisabled"
          @click="emit('jump')"
        />
      </PmButtonListPure>
    </template>
  </PmNotificationContainerPure>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { min, max } from 'date-fns'

import { startEndDate } from '@/utilities/date'

import PmNotificationContainerPure, {
  type Props as PropsNotificationContainerPure,
} from '@/components/PmNotification/PmNotificationContainerPure.vue'

import PmButtonPure from '@/components/basics/PmButtonPure.vue'
import PmButtonListPure from '@/components/basics/PmButtonListPure.vue'
import PmMetaListItemPure from '@/components/PmMetaList/PmMetaListItemPure.vue'
import type { ResourceAllocation } from 'generated/graphql'
import type { Nilable } from '@/types/misc'
import PmFromToPure from '@/components/persoplan/PmFromTo/PmFromToPure.vue'
import PmNotePure from '@/components/PmNote/PmNotePure.vue'

export interface Job {
  id: number
  startDate: Date
  endDate: Date
}

export interface Props
  extends Pick<PropsNotificationContainerPure, 'date' | 'isUnread'> {
  name: string
  message: string | undefined
  projectNumber: string
  projectName: string
  jobs?: Job[]
  resourceAllocations?: ResourceAllocation[]
}

const props = withDefaults(defineProps<Props>(), {})

export type Emits = {
  openDetails: []
  jump: []
}

const emit = defineEmits<Emits>()

const isSingleJob = computed(() => {
  if (!props.jobs) return
  return props.jobs.length <= 1
})

const timeframe = computed(() => {
  if (!props.jobs) return
  if (props.jobs.length === 0) return

  if (isSingleJob.value) {
    const firstJob = props.jobs[0]
    return startEndDate(firstJob.startDate, firstJob.endDate, {
      showDateAnyway: true,
    })
  }

  // Now it's multiple jobs
  const allStartDates = props.jobs.map((job) => job.startDate)
  const earliestStartDate = min(allStartDates)

  const allEndDates = props.jobs.map((job) => job.endDate)
  const latestEndDate = max(allEndDates)

  return startEndDate(earliestStartDate, latestEndDate, {
    showDateAnyway: true,
  })
})

const isJumpDisabled = computed(() => {
  if (isSingleJob.value !== true) return true
  return false
})

const jumpButtonTitle = computed(() => {
  if (isSingleJob.value !== true) return
  return 'zum Job springen'
})
</script>

<style lang="scss">
.PmNotificationResourceRequestMessagePure {
  $block: &;
}
</style>
